'use strict'

import dayjs from 'dayjs'

/**
 * Handles export flows (JPEG/PDF/project).
 * Keeps the export side-effects separate from UI wiring.
 */
class ExportHandler {
    /**
     * @param {Object} application - Visualizer Application instance.
     */
    constructor(application) {
        this.app = application
    }

    /**
     * Generate a JPEG using the dynamic PDF generator.
     * Uses the same generator as PDF but requests a single image output.
     * @returns {Promise<void>}
     */
    async generateJpeg() {
        await this._generateDocument({
            output: 'jpg',
            pagesKey: 'pagesForJpg',
            pages: 1,
            errorLabel: 'JPG',
        })
    }

    /**
     * Generate a PDF export using the dynamic PDF generator.
     * @returns {Promise<void>}
     */
    async generatePdf() {
        await this._generateDocument({
            output: 'pdf',
            pagesKey: 'pagesForPdf',
            pages: 2,
            errorLabel: 'PDF',
        })
    }

    async _generateDocument({ output, pagesKey, pages, errorLabel }) {
        try {
            const app = this.app
            const pdfConfig = app.config.pdf
            const companyAddress = app.company.address || {}
            const fileName = pdfConfig.js_filename || 'Pdf'

            const params = {
                assetPath: app.assetPath,
                pdfConfig,
                pdfTranslation: app.pdf_translation,
                locale: app.currentLocale,
                subdomain: app.subdomain,
                colorSample: app.canvasState.colorSample,
                footer: {
                    name: app.company.name || '',
                    street1: companyAddress.street1 || '',
                    npa: companyAddress.npa || '',
                    city: companyAddress.city || '',
                    email: app.company.email || '',
                },
                output,
                [pagesKey]: pages,
            }

            const { default: Gen } = await import(/* webpackChunkName: 'pdf' */ `./../pdf/${fileName}.js`)
            await new Gen().generate(params)
        } catch (err) {
            console.warn(`${errorLabel} generation failed:`, err)
        }
    }

    /**
     * Export the current project to a downloadable file.
     * @param {HTMLInputElement|null} nameInput - Optional input containing the desired filename.
     */
    exportProject(nameInput) {
        const app = this.app
        const name = (nameInput?.value || app.project.name).trim()
        const datedName = `${name}_${dayjs().format('DD_MM_YYYY_HH_mm_ss')}`
        const uri = app.exportObj.export_template(1, app.canvasState)
        this._downloadFile(uri, `${datedName}.${app.exportObj.extensionFile}`)
    }

    /**
     * Trigger a client-side download of provided data.
     * @param {string} data - File contents (URI or plain).
     * @param {string} fileName - Desired filename.
     * @param {string} [type='text/xml;charset=UTF-8'] - MIME type.
     * @private
     */
    _downloadFile(data, fileName, type = 'text/xml;charset=UTF-8') {
        const a = document.createElement('a')
        const blobUrl = window.URL.createObjectURL(
            new Blob([data], {
                type,
            }),
        )

        a.style.display = 'none'
        a.href = blobUrl
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(blobUrl)
    }
}

export default ExportHandler
